import React from 'react';
import { Expand } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocale } from '../contexts/LocaleContext';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { Kbd } from './ui/kbd';
import { ViewHeader } from './ViewHeader';
import { EditableDocumentHeader, type SwitchableDocument } from './EditableDocumentHeader';
import { Button } from './ui/button';
import { PanelRight, PanelLeft } from 'lucide-react';
import { EditorRightSidebar } from './EditorRightSidebar';
import { useRightSidebar } from '../contexts/RightSidebarContext';
import { useSidebar } from './ui/sidebar';
import { useProjectPageTitle } from '../hooks/usePageTitle';

interface DocumentHeaderProps {
  documentTitle: string;
  projectTitle: string;
  documentIcon: string | null;
  onDocumentTitleChange: (title: string) => void;
  onProjectTitleChange: (title: string) => void;
  onIconChange: (icon: string | null) => void;
  isReadOnly?: boolean;
  onFocusMode?: () => void;
  showFocusMode?: boolean;
  showRightSidebarToggle?: boolean;
  ownerEmail?: string | null;
  hasCommentPermission?: boolean;
  isMainDocument?: boolean;
  sectionLabel?: string | null;
  sectionIcon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  documents?: SwitchableDocument[];
  currentDocumentId?: string | null;
  mainDocumentId?: string | null;
  onSelectDocument?: (documentId: string) => void;
  onCreateDocument?: () => void;
  canCreateDocument?: boolean;
}

const RightSidebarToggleButton: React.FC = () => {
  const { t } = useLocale();
  let rightSidebar;
  try {
    rightSidebar = useRightSidebar();
  } catch {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={rightSidebar.toggleSidebar}
      className="h-10 w-10 text-foreground"
      aria-label={t('toggle_sidebar')}
      title={t('toggle_sidebar')}
    >
      <PanelRight className="h-4 w-4" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
};

export const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  documentTitle,
  projectTitle,
  documentIcon,
  onDocumentTitleChange,
  onProjectTitleChange,
  onIconChange,
  isReadOnly = false,
  onFocusMode,
  showFocusMode = true,
  showRightSidebarToggle = false,
  ownerEmail,
  hasCommentPermission = false,
  isMainDocument = false,
  sectionLabel = null,
  sectionIcon,
  documents,
  currentDocumentId,
  mainDocumentId,
  onSelectDocument,
  onCreateDocument,
  canCreateDocument,
}) => {
  const isMobile = useIsMobile();
  const { t } = useLocale();
  useProjectPageTitle(projectTitle, documentTitle || 'Untitled', sectionLabel);
  let sidebar;
  try {
    sidebar = useSidebar();
  } catch {
    sidebar = null;
  }

  const rightContent = showFocusMode && onFocusMode ? (
    !isMobile ? (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onFocusMode}
              className="flex items-center gap-2 text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
              aria-label={t('focus_mode')}
            >
              <span>{t('focus_mode')}</span>
              <Expand className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>{t('hide_tools')}</span>
              <Kbd>
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} .
              </Kbd>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onFocusMode}
          className="h-10 w-10 text-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label={t('focus_mode')}
          title={t('focus_mode')}
        >
          <Expand className="h-4 w-4" />
        </Button>
        {showRightSidebarToggle && <RightSidebarToggleButton />}
      </div>
    )
  ) : undefined;

  return (
    <ViewHeader rightContent={rightContent} marginBottom="0">
      {isMobile && sidebar && (
        <button
          onClick={sidebar.toggleSidebar}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            borderRadius: '0.375rem',
            flexShrink: 0,
            marginRight: '0.5rem',
          }}
          aria-label={t('toggle_sidebar')}
          title={t('toggle_sidebar')}
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      )}
      <EditableDocumentHeader
        documentTitle={documentTitle}
        projectTitle={projectTitle}
        documentIcon={documentIcon}
        onDocumentTitleChange={onDocumentTitleChange}
        onProjectTitleChange={onProjectTitleChange}
        onIconChange={onIconChange}
        isReadOnly={isReadOnly}
        ownerEmail={ownerEmail}
        hasCommentPermission={hasCommentPermission}
        isMainDocument={isMainDocument}
        sectionLabel={sectionLabel}
        sectionIcon={sectionIcon}
        documents={documents}
        currentDocumentId={currentDocumentId}
        mainDocumentId={mainDocumentId}
        onSelectDocument={onSelectDocument}
        onCreateDocument={onCreateDocument}
        canCreateDocument={canCreateDocument}
      />
    </ViewHeader>
  );
};

